import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { TAXONOMY, findGroupForCategory } from "@/data/taxonomy";
import CategoryMultiPicker from "@/components/CategoryMultiPicker";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function ListYourBusiness() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    category: "",
    business_address: "",
    business_city: "",
    business_state: "",
    description: "",
    owner_email: "",
    website: "",
    phone: "",
    contact_email: "",
    date_established: "",
    categories: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (form.categories?.length) {
        payload.categories = form.categories;
        payload.category_groups = Array.from(new Set(form.categories.map((c) => findGroupForCategory(c) || c)));
        payload.category = form.categories[0];
        payload.category_group = payload.category_groups[0];
      }
      const { data } = await api.post("/businesses/submit", payload);
      setSubmitted(data);
      toast.success("Listing published as Unclaimed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <Check className="mx-auto text-emerald-500" size={48} />
        <h1 className="font-display text-4xl mt-6">You're on the map.</h1>
        <p className="mt-3 text-gray-600">Your listing is live in the directory — marked as Unclaimed until you register with <strong>{form.owner_email}</strong> to claim it.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to={`/businesses/${submitted.id}`}><Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="submitted-view-listing">View listing</Button></Link>
          <Link to={`/register?claim=${encodeURIComponent(form.owner_email)}`}>
            <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="submitted-claim-now">Claim now</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <Badge variant="outline" className="rounded-sm overline text-[#B31E1E] border-[#B31E1E]/30 mb-4">
        Free directory listing · no card required
      </Badge>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">List your business</h1>
      <p className="mt-3 text-gray-600 max-w-lg">No password needed right now. Paste in the basics and your business appears in the public directory immediately as <strong>Unclaimed</strong>. Anyone can leave a review. Claim it later with the same email to manage and unlock subscription features.</p>

      <Card className="mt-10 rounded-sm border border-border p-6">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="business_name">Business name</Label>
            <Input id="business_name" required value={form.business_name} onChange={update("business_name")} className="rounded-sm mt-1" data-testid="list-business-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label>Categories (up to 10)</Label>
              <div className="mt-1">
                <CategoryMultiPicker
                  value={form.categories || []}
                  onChange={(arr) => setForm({ ...form, categories: arr })}
                  testIdPrefix="list-category"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="owner_email">Your email (for future claim)</Label>
              <Input id="owner_email" type="email" required value={form.owner_email} onChange={update("owner_email")} className="rounded-sm mt-1" data-testid="list-email-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="business_address">Street address</Label>
            <Input id="business_address" required value={form.business_address} onChange={update("business_address")} className="rounded-sm mt-1" data-testid="list-address-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="business_city">City</Label>
              <Input id="business_city" value={form.business_city} onChange={update("business_city")} className="rounded-sm mt-1" data-testid="list-city-input" />
            </div>
            <div>
              <Label htmlFor="business_state">State</Label>
              <Input id="business_state" placeholder="CA" value={form.business_state} onChange={update("business_state")} className="rounded-sm mt-1" data-testid="list-state-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} className="rounded-sm mt-1" data-testid="list-phone-input" />
            </div>
            <div>
              <Label htmlFor="website">Website (optional)</Label>
              <Input id="website" type="url" placeholder="https://" value={form.website} onChange={update("website")} className="rounded-sm mt-1" data-testid="list-website-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="contact_email">Public contact email (optional)</Label>
            <Input id="contact_email" type="email" value={form.contact_email} onChange={update("contact_email")} className="rounded-sm mt-1" data-testid="list-contact-email-input" />
          </div>
          <div>
            <Label htmlFor="date_established">Year established</Label>
            <Input id="date_established" type="number" min="1850" max="2100" step="1" placeholder="2015" value={form.date_established} onChange={update("date_established")} className="rounded-sm mt-1 max-w-xs" data-testid="list-date-established-input" />
          </div>
          <div>
            <Label htmlFor="description">Short description</Label>
            <Textarea id="description" rows={3} value={form.description} onChange={update("description")} className="rounded-sm mt-1" data-testid="list-description-input" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="list-submit-button">
            {submitting ? "Publishing..." : "Publish listing"}
          </Button>
        </form>
      </Card>

      <p className="mt-6 text-sm text-gray-600">
        Prefer to claim instantly? <Link to="/register" className="text-[#B31E1E] hover:underline" data-testid="list-link-register">Create a full business account</Link>.
      </p>
    </div>
  );
}
